import { IndexedDbUnavailableError } from '@/infrastructure/indexeddb/IndexedDbUnavailableError';

/**
 * Banco único do Activation Runtime (Spec Técnica > Modelo de dados),
 * compartilhado pelas 3 object stores desta fatia. Usa a API nativa
 * `indexedDB` diretamente (sem biblioteca adicional) — disponível tanto em
 * `window` quanto em `ServiceWorkerGlobalScope`, condição exigida para que
 * `SyncLeadQueueUseCase` seja importável de ambos os contextos.
 */
export const ACTIVATION_RUNTIME_DB_NAME = 'activation-runtime-v1';
export const ACTIVATION_RUNTIME_DB_VERSION = 1;

export const LEAD_QUEUE_STORE = 'leadQueue';
export const SYNC_BOOKKEEPING_STORE = 'syncBookkeeping';
export const ACTIVE_SESSION_STORE = 'activeSession';
export const ACTIVE_SESSION_KEY = 'current';

function createStores(db: IDBDatabase): void {
  if (!db.objectStoreNames.contains(LEAD_QUEUE_STORE)) {
    const leadQueueStore = db.createObjectStore(LEAD_QUEUE_STORE, { keyPath: 'id' });
    leadQueueStore.createIndex('eventId', 'eventId', { unique: false });
    leadQueueStore.createIndex('createdAt', 'createdAt', { unique: false });
  }

  if (!db.objectStoreNames.contains(SYNC_BOOKKEEPING_STORE)) {
    const bookkeepingStore = db.createObjectStore(SYNC_BOOKKEEPING_STORE, { keyPath: 'deviceId' });
    bookkeepingStore.createIndex('eventId', 'eventId', { unique: false });
  }

  if (!db.objectStoreNames.contains(ACTIVE_SESSION_STORE)) {
    // Chave fixa única fora do registro (out-of-line) — `ActiveSessionSnapshot`
    // não tem um campo próprio de identidade (Spec Técnica > Modelo de dados).
    db.createObjectStore(ACTIVE_SESSION_STORE);
  }
}

/**
 * Abre (criando/migrando se necessário) o banco `activation-runtime-v1`.
 * Rejeita com `IndexedDbUnavailableError` em qualquer falha (cota excedida,
 * permissão negada, corrupção) — CA-03.
 */
export function openActivationRuntimeDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(ACTIVATION_RUNTIME_DB_NAME, ACTIVATION_RUNTIME_DB_VERSION);
    } catch (error) {
      reject(new IndexedDbUnavailableError('Falha ao abrir o IndexedDB.', error));
      return;
    }

    request.onupgradeneeded = () => createStores(request.result);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      reject(
        new IndexedDbUnavailableError(
          request.error?.message ?? 'Falha desconhecida ao abrir o IndexedDB.',
          request.error
        )
      );
    };
    request.onblocked = () => {
      reject(new IndexedDbUnavailableError('Abertura do IndexedDB bloqueada por outra conexão.'));
    };
  });
}

/** Promisifica um `IDBRequest`, rejeitando com `IndexedDbUnavailableError`. */
export function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      reject(
        new IndexedDbUnavailableError(
          request.error?.message ?? 'Falha desconhecida na operação de IndexedDB.',
          request.error
        )
      );
    };
  });
}

/** Promisifica uma `IDBTransaction` completa (usado quando a operação em si
 * já foi disparada e só resta aguardar o commit/erro da transação). */
export function promisifyTransaction(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => {
      reject(
        new IndexedDbUnavailableError(
          transaction.error?.message ?? 'Falha desconhecida na transação de IndexedDB.',
          transaction.error
        )
      );
    };
    transaction.onabort = () => {
      reject(
        new IndexedDbUnavailableError(
          transaction.error?.message ?? 'Transação de IndexedDB abortada.',
          transaction.error
        )
      );
    };
  });
}
